<template>
  <div @click="selectedItem = null">
    <breadcrumbs :home="recurring ? 'Recurring shipment template' : 'Shipment'">
      <template>
        <v-progress-linear striped height="6" class="mx-16" v-model="itemProgress" v-if="itemProgress != 100" style="max-width:600px"/>
      </template>
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <v-card>
          <v-card width="400">
            <sales-preferences @update="preferences.defaultMessage=$event.defaultMessage;preferences.defaultCreateInvoice=$event.defaultCreateInvoice;preferences.allowDiscounts=$event.allowDiscounts;preferences.alwaysInBond=$event.alwaysInBond;preferences.useNumber=$event.useNumber;preferences.defaultQBO=$event.defaultQBO;preferences.defaultAllowOnlinePayments=$event.defaultAllowOnlinePayments;preferences.defaultTaxInclusive=$event.defaultTaxInclusive"/>
          </v-card>
          </v-card>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row v-if="recurring">
            <v-col><recurring-transaction v-model="recurringTransaction"/></v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="5" md="4">
              <v-row>
                <v-col>
                  <v-autocomplete v-model="shipment.customerId" outlined dense label="Customer" :rules="[selectCustomer]"
                                  :items="customersList" item-text="name" item-value="id" @change="changeCustomer" no-data-text="No customers found"
                                  :hint="customerHint(customer)" persistent-hint ref="customer">
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="customerCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new customer
                      </div>
                    </template>
                    <template v-slot:item="{item}">
                      <v-list-item-content>{{item.name}}</v-list-item-content>
                      <v-list-item-action class="text-caption">{{customerHint(item)}}</v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="10">
                  <v-text-field label="Email" v-model="shipment.email" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                hint="Separate emails with a comma"/>
                </v-col>
                <v-col cols="2" class="text-caption mt-3">
                  <v-menu :close-on-content-click="false" min-width="350" :nudge-bottom="20" :disabled="!edit">
                    <template v-slot:activator="{ on }">
                      <a href="#" v-on="on">Cc/Bcc</a>
                    </template>
                    <v-card class="pa-4">
                      <v-text-field label="Cc" v-model="shipment.cc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                      <v-text-field label="Bcc" v-model="shipment.bcc" :rules="[fieldRules.maxLength(100)]" outlined dense :counter="100"
                                     hint="Separate emails with a comma"/>
                    </v-card>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Bill to address" v-model="shipment.billToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea label="Ship to address" v-model="shipment.shipToAddress" outlined dense no-resize :rows="3" :rules="[fieldRules.maxLength(250)]" hide-details="auto"/>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6" md="5" offset-sm="1">
              <v-row dense>
                <v-col cols="6" v-if="!recurring">
                  <date-picker v-model="shipment.date" label="Shipment date/time" @change="changeDate()"/>
                </v-col>
                <v-col cols="6">
                  <v-text-field label="Reference #" v-model="shipment.number" outlined dense :rules="[fieldRules.maxLength(20)]"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field label="Customer PO" v-model="shipment.po" outlined dense :rules="[fieldRules.maxLength(20)]" counter="20"/>
                </v-col>
                <v-col cols="6">
                  <v-combobox label="Ship via" v-model="shipment.shipVia" outlined dense :rules="[fieldRules.maxLength(20)]" counter="20" :items="shipVias"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="3">
                  <decimal-number-input label="Pallets" v-model="shipment.pallets" prefix="" :precision="0"/>
                </v-col>
                <v-col cols="3">
                  <v-checkbox v-model="shipment.delivered" label="Delivered" dense class="ml-2"/>
                </v-col>
                <v-col cols="6">
                  <shipment-zones-select v-model="shipment.shipmentZoneId" label="Shipment zone" allow-add/>
                </v-col>
              </v-row>
              <v-row v-if="sites.length>1" dense>
                <v-col>
                  <v-autocomplete v-model="shipment.siteId" outlined dense label="Ship from site" @change="changeSite()"
                                  :items="sites" item-text="name" item-value="id"/>
                </v-col>
              </v-row>

              <v-row dense align="center">
                <v-col cols="6" sm="5" style="position:relative">
                  <div id="create-invoice">
                    <v-checkbox label="Create invoice" v-model="createInvoice" :append-icon="createInvoice?'mdi-details':''"
                                @change="createInvoice?showInvoice=true:''" @click:append="createInvoice?showInvoice=true:''"
                                :disabled="!values || alreadyInvoiced"/>
                  </div>
                  <v-menu v-model="showInvoice" :close-on-content-click="false" attach="#create-invoice" min-width="480" eager>
                    <v-card class="pa-4">
                      <sales-invoices-details :invoice="invoice" :preferences="preferences" :customer="customer"
                                              :restrictedToSalesRep.sync="restrictedToSalesRep" :recurring="recurring"
                                              :cc.sync="shipment.cc" :bcc.sync="shipment.bcc"/>
                    </v-card>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row v-if="alreadyInvoiced && edit">
                <v-col class="warning--text">An invoice cannot be created because items on this shipment have already been invoiced.</v-col>
              </v-row>
            </v-col>

            <v-col class="text-h6 text-right pr-6" v-if="createInvoice && $vuetify.breakpoint.mdAndUp">
              Amount {{customer && this.company.settings.homeCurrency !== customer.currency ? customer.currency : ''}} {{total | currency}}
              <div class="text-body-1" v-if="invoice.number">Invoice #{{invoice.number}}</div>
              <div class="text-h5 green--text" v-if="paid">PAID</div>
            </v-col>

          </v-row>

          <v-row dense v-if="createInvoice && values">
            <v-col cols="1" v-if="$vuetify.breakpoint.mobile">
              <scan-code @code="scan" :products="products" :items="items"/>
            </v-col>
            <v-spacer/>
            <v-col cols="10" sm="5" lg="3">
              <v-switch label="Amounts are inclusive of tax" v-model="invoice.taxInclusive" dense hide-details class="mt-0" @change="toggleTaxInclusive()"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <outlined-block v-model="shipment.lines" :rules="[lines]" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="shipment.lines" item-key="key" no-data-text="There must be at least one item to ship"
                              :headers="lineHeaders" :expanded.sync="expanded"
                              :item-class="item => item.key === selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              class="no-sort" ref="lineTableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:header.add>
                    <scan-code @code="scan" :products="products" :items="items"/>
                  </template>
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n3" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n3" icon @click.stop="deleteLine(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.key">
                      <multi-level-select :menuData="types" :first.sync="item.type" :second.sync="item.id" caption @change="changeLineType(item)" outlined label="Item"/>
                      <template v-if="item.type==='Product'">
                        <v-checkbox dense v-model="item.inBondProduct" class="mt-n4" :disabled="!isProductLoaded(item)"
                                    @change="changeProductBonded(item)">
                          <template v-slot:label><span class="text-caption">Take product from in-bond inventory</span></template>
                        </v-checkbox>
                      </template>
                      <v-text-field dense label="Description" v-model="item.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" placeholder="Description" class="text-caption" outlined/>
                    </template>
                    <div v-else class="pl-1">
                      <div class="text-caption" :class="{'error--text':!item.type || !item.id}">{{itemDescription(item)}}{{item.type==='Product' ? (item.inBondProduct ? ' (in-bond)' : ' (duty-paid)') : ''}}</div>
                      <div :class="{'error--text':!item.description}">{{item.description || 'Description required'}}</div>
                    </div>
                  </template>
                  <template v-slot:item.sku="{item}">
                    <template v-if="!item.items">
                      <v-text-field v-if="selectedItem === item.key" label="SKU" dense outlined v-model="item.sku" class="text-caption" :rules="[fieldRules.maxLength(20)]"/>
                      <div v-else class="pl-1">{{item.sku}}</div>
                    </template>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input label="Quantity"
                                             v-model="item.quantity" prefix="" :disabled="isBulkContainer(item)"
                                            :suffix="suffix(item)" :precision="precision(item)" class="text-caption" @change="changeQuantityPrice(item)"
                                            :no-minimum="item.type==='Item'"/>
                      <v-checkbox v-if="item.type==='Product'" dense v-model="item.cases" @change="setProductPrice(item)" class="mt-n4">
                        <template v-slot:label><span class="text-caption">cases</span></template>
                      </v-checkbox>
                      <v-combobox v-if="lotTracking(item)" label="Lot" :items="currentLots(item)" :return-object="false" item-text="lot" item-value="lot" v-model="item.lot" dense :rules="[fieldRules.required,fieldRules.maxLength(20)]" class="text-caption" outlined>
                        <template v-slot:item="{item:lotInfo}">
                          <v-list-item-content class="text-caption">{{lotInfo.lot}}</v-list-item-content>
                          <v-list-item-action class="text-caption">{{lotInfo.quantity | number(precision(item))}} {{suffix(item)}}</v-list-item-action>
                        </template>
                      </v-combobox>
                    </template>
                    <div v-else class="pr-1">
                      <div :class="{'error--text':item.quantity < 0 && item.type!=='Item'}">{{item.quantity | number(precision(item))}} {{suffix(item)}}</div>
                      <div v-if="lotTracking(item)" class="text-caption" :class="{'error--text':!item.lot}">Lot: {{item.lot}}</div>
                    </div>
                  </template>
                  <template v-slot:item.so="{item}">
                    <v-select v-if="selectedItem === item.key" label="Sales order #"
                              :items="salesOrdersForLine(item)" item-text="soNumber" item-value="salesOrderId" :return-object="false" v-model="item.salesOrderId"
                              no-data-text="" outlined
                              class="text-caption" dense @change="changedSO(item)"/>
                    <div v-else class="mr-1">{{salesOrderForLine(item)}}</div>
                  </template>
                  <template v-slot:item.price="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Unit price" v-model="item.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(item)"/>
                      <div v-else class="pr-1" :class="{'error--text':item.unitPrice<0}">${{item.unitPrice | number(4)}}</div>
                    </template>
                  </template>
                  <template v-slot:item.amount="{item}">
                    <template v-if="!item.items">
                      <decimal-number-input v-if="selectedItem === item.key" label="Amount" v-model="item.amount" class="text-caption" @change="changeAmount(item)" :no-minimum="item.type==='Item'"/>
                      <div v-else class="pr-1" :class="{'error--text':item.amount<0 && item.type!=='Item'}">{{item.amount | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.taxes="{item}">
                    <template v-if="!item.items">
                      <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" outlined v-model="item.taxCodeId" :rules="[connectedToAccounting?fieldRules.required:true]"
                                :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                      <div v-else class="pl-1" :class="{'error--text':connectedToAccounting && !item.taxCodeId}">{{itemTax(item)}}</div>
                    </template>
                  </template>
                  <template v-slot:expanded-item="{headers,item}">
                    <td :colspan="headers.length" class="pa-0">
                      <v-card flat @click:stop="() => selectedItem = item.key">
                        <v-simple-table dense style="width:100%">
                          <tr v-for="bundleItem in item.items" :key="bundleItem.key" :class="selectedItem === item.key ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''">
                            <td :width="headers[0].width" :min-width="headers[0].width" class="pa-0 v-data-table__divider text-center"/>
                            <td :width="headers[1].width" :min-width="headers[1].width" class="px-1 py-2 v-data-table__divider">
                              <v-text-field v-if="selectedItem === item.key" dense v-model="bundleItem.description" :rules="[fieldRules.required,fieldRules.maxLength(100)]" label="Description" class="text-caption" outlined hide-details/>
                              <div v-else class="pl-1">{{bundleItem.description}}</div>
                            </td>
                            <td :width="headers[2].width" :min-width="headers[2].width" class="px-1 py-2 v-data-table__divider">
                              <v-text-field v-if="selectedItem === item.key" dense v-model="bundleItem.sku" label="SKU" class="text-caption" :rules="[fieldRules.maxLength(20)]" outlined hide-details/>
                              <div v-else class="pl-1">{{bundleItem.sku}}</div>
                            </td>
                            <td :width="headers[3].width" :min-width="headers[3].width" class="px-2 v-data-table__divider text-right">
                              {{item.quantity}} x {{bundleItem.quantity | number(precision(bundleItem))}} {{suffix(bundleItem)}}
                            </td>
                            <td :width="headers[4].width" :min-width="headers[4].width" class="px-1 v-data-table__divider">&nbsp;</td>
                            <template v-if="createInvoice && values">
                              <td :width="headers[5].width" :min-width="headers[5].width" class="px-1 py-2 v-data-table__divider text-right">
                                <decimal-number-input v-if="selectedItem === item.key" outlined label="Unit price" v-model="bundleItem.unitPrice" :precision="4" class="text-caption" @change="changeQuantityPrice(bundleItem, item)" hide-details/>
                                <div v-else class="pl-1">{{bundleItem.unitPrice | currency}}</div>
                              </td>
                              <td :width="headers[6].width" :min-width="headers[5].width" class="px-1 py-2 v-data-table__divider text-right">
                                <decimal-number-input v-if="selectedItem === item.key" outlined label="Amount" v-model="bundleItem.amount" class="text-caption" @change="changeAmount(bundleItem)" :no-minimum="bundleItem.type==='Item'" hide-details/>
                                <div v-else class="pl-1">{{bundleItem.amount | currency}}</div>
                              </td>
                              <td :width="headers[7].width" :min-width="headers[7].width" class="px-1 v-data-table__divider">
                                <v-autocomplete v-if="selectedItem === item.key" dense label="Tax" v-model="bundleItem.taxCodeId" :rules="[fieldRules.required]" outlined hide-details
                                          :items="taxes" item-text="name" item-value="id" class="text-caption" @change="calculateTotals(true)"/>
                                <div v-else class="pl-1">{{itemTax(bundleItem)}}</div>
                              </td>
                            </template>
                          </tr>
                        </v-simple-table>
                      </v-card>
                    </td>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab color="primary" x-small @click.stop="addLine" :disabled="!addOkay || !edit"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" order="2" order-sm="1">
              <v-container class="pa-0">
                <v-row>
                  <v-col>
                    <v-textarea label="Message to customer" v-model="shipment.memo" outlined dense rows="2" :rules="[fieldRules.maxLength(1000)]" hide-details="auto" class="mt-4"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Special instructions" rows="2"
                                  v-model="shipment.specialInstructions" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea label="Private note" rows="2"
                                  v-model="shipment.privateNote" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="1">
                    <attachment v-model="shipment.attachmentURL" :read-only="!edit" @change="shipment.attachmentUpload=shipment.attachmentURL" @delete="shipment.attachmentDelete=true"/>
                  </v-col>
                  <v-spacer/>
                  <v-col cols="6" sm="4" md="3" v-if="customer && company.settings.homeCurrency !== customer.currency && createInvoice && values">
                    <decimal-number-input class="text-caption" :label="`1 ${customer.currency} =`" outlined v-model="invoice.exchangeRate" prefix="" :suffix="` ${company.settings.homeCurrency}`" :precision="6" hide-details/>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" sm="6" order="1" order-sm="2" v-if="createInvoice && values">
              <v-container class="pa-0">
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Subtotal</v-col>
                  <v-col cols="4" class="text-right pr-6">{{subTotal | currency}}</v-col>
                </v-row>
                <v-row dense align="center" v-if="preferences.allowDiscounts || invoice.discount.amount">
                  <v-col cols="5" md="4" offset-md="2">
                    <v-select v-model="invoice.discount.percent" :items="[{text:'Discount percent',value:true},{text:'Discount value',value:false}]"
                              dense outlined hide-details class="text-caption"
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="3" md="2">
                    <decimal-number-input v-model="invoice.discount.amount"
                              :prefix="invoice.discount.percent?'':'$'" :suffix="invoice.discount.percent?'%':''"
                              :max="invoice.discount.percent?100:undefined"
                              hide-details caption
                              @change="calculateTotals(true)"/>
                  </v-col>
                  <v-col cols="4" class="text-right pr-6">{{discountValue?'-':''}}{{discountValue | currency}}</v-col>
                </v-row>
                <template v-for="tax of shipment.taxLines">
                  <v-row dense v-if="tax.name!=='Exempt' && tax.name!=='Out of Scope'" :key="tax.id" align="center">
                    <v-col cols="8" class="font-weight-medium text-right">{{invoice.taxInclusive?'Includes ':''}}{{tax.name}} @{{tax.percent}}% on {{tax.onAmount | currency}}</v-col>
                    <v-col cols="3" offset="1" class="text-right pr-6">
                      <decimal-number-input v-model="tax.amount" hide-details no-minimum/>
                    </v-col>
                  </v-row>
                </template>
                <v-row dense>
                  <v-col cols="8" class="font-weight-medium text-right">Total {{customer && company.settings.homeCurrency !== customer.currency ? `(${customer.currency})` : ''}}</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total | currency}}</v-col>
                </v-row>
                <v-row dense v-if="customer && company.settings.homeCurrency !== customer.currency">
                  <v-col cols="8" class="font-weight-medium text-right">Total ({{company.settings.homeCurrency}})</v-col>
                  <v-col cols="4" class="text-right pr-6">{{total*invoice.exchangeRate | currency}}</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="!shipmentId || !shipment.recurringTransaction">
          <v-switch v-if="shipmentId && company.permissions.includes('selling.write') && !originalDraft"
                    v-model="edit" label="Edit" hide-details class="mt-0 mr-4"
                    :disabled="paid || dirty || loading"/>
          <v-switch dense hide-details label="Make recurring" class="mt-0" v-model="recurring"
                    v-if="edit && company.allowRecurringTransactions && (!shipmentId || !shipment.recurringTransaction)" :disabled="onlyRecurring"/>
        </template>
        <v-spacer/>
        <v-checkbox v-if="edit" dense hide-details label="Draft" v-model="shipment.draft" class="mr-2 mt-n1"/>
        <v-btn :plain="edit" color="primary" @click="cancel" :disabled="loading">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save()" :disabled="itemProgress!==100">Save{{recurring?' template':''}}</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <customer-quick-create v-model="customerCreate" @create="newCustomer" :sales-rep-id="restrictedToSalesRep"/>

    <v-dialog v-model="showMoveProduct" persistent max-width="900">
      <v-card>
        <v-card-title>Move product</v-card-title>
        <v-divider/>
        <v-card-text class="pa-1">
          <v-list-item class="text-body-1 my-3">
            There is insufficient duty-paid product to ship.
            You may, however, move product from in-bond inventory first.
          </v-list-item>
          <v-list-item>
            <outlined-block>
              <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                            fixed-header height="300"
                            :items="moveProductToDutyPaid"
                            :headers="[{text: 'Product',value:'name',width:'35%'},
                                      { text: 'Required', value: 'required', width:'15%',align:'right'},
                                      { text: 'Available', value: 'available' ,width:'15%',align:'right'},
                                      { text: 'Move', value: 'move' ,width:'15%'},
                                      { text: 'Lot', value: 'lot' ,width:'20%'}]"
                            class="no-sort mt-1">
                <template v-slot:item.move="{item}">
                  <decimal-number-input v-if="item.lot" integer
                                        :outlined="false" v-model="item.bottles" prefix="" suffix="bottles"
                                        :precision="0" class="text-body-2 mt-n1" hide-details/>
                  <template v-else>None available</template>
                </template>
                <template v-slot:item.lot="{item}">
                  <v-combobox v-if="item.lot" :items="item.inBondLots" v-model="item.lot" dense hide-details class="text-body-2 mt-n1"/>
                </template>
              </v-data-table>
            </outlined-block>
          </v-list-item>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-6">
          <v-spacer/>
          <v-btn plain @click="moveProductToDutyPaid=[];showMoveProduct=false">Cancel</v-btn>
          <v-btn color="primary" @click="showMoveProduct=false;save2()">Continue</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns'
import sortableJS from 'sortablejs';
import CustomerQuickCreate from '@/views/sales/customers/CustomerQuickCreate'
import confirmLeave from '@/mixins/confirmLeave'
import customerTypes from '@/mixins/customerTypes'
import createHierarchyList from '@/mixins/createHierarchyList'
import SalesPreferences from '@/views/sales/SalesPreferences'
import SalesInvoicesDetails from '@/views/sales/invoices/SalesInvoicesDetails'
import RecurringTransaction from '@/components/RecurringTransaction'
import Attachment from '@/components/Attachment'
import ShipmentZonesSelect from '@/views/sales/shipmentZones/SalesShipmentZonesSelect'

export default {
  name: 'SalesShipmentsEdit',
  mixins: [confirmLeave,createHierarchyList,customerTypes],
  components: {CustomerQuickCreate,SalesInvoicesDetails,SalesPreferences,RecurringTransaction,Attachment,ShipmentZonesSelect},
  props: {
    shipmentId: {type:String, required:false},
    salesOrderId: {type:String, required:false},
    estimateId: {type:String, required:false},
    customerId: {type:String, required:false},
    itemType: {type:String, required:false},
    copyShipmentId: {type:String, required:false},
    onlyRecurring: {type:Boolean, required:false},
    fromRecurringTransactionId: {type:String, required:false},
    date: {type:String, required:false}
  },
  data () {
    return {
      preferences: {},
      edit: false,
      recurring: this.onlyRecurring,
      recurringTransaction: {},
      province: null,
      taxes: [],
      shipVias: [],
      customers: [],
      customerCreate:false,
      showInvoice: false,

      spirits: [],
      wines: [],
      nonExcises: [],
      allBulkContainers: [],
      allTanks: [],
      items: [],
      bundles: [],
      products: [],
      customerTypes: [],
      itemCategories: [],
      productCategories: [],
      shipment: {customerId:'',billToAddress:'',shipToAddress:'',draft:false, delivered:true, email:'', cc:'', bcc:'',
                  number:'',pallets:0,specialInstructions:'',
                  date:this.date ? `${this.date} 00:00:00` : format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                  po:'',siteId:'',shipVia:'',lines:[],taxLines:[],memo:'',privateNote:'',shipmentZoneId:''},
      createInvoice: false,
      originalInvoice: false,
      invoice: {date:'',due:'',accountRef:'',paymentMethodId:'',termId:'',salesRepId:'',exchangeRate:1,taxInclusive:false,qbo:false,allowOnlinePayments:true,paymentRefNum:'',discount:{percent:true,amount:0}},
      paid: false,
      shipmentInput: null,
      moveProductToDutyPaid: null,
      showMoveProduct: false,
      dirty: false,
      saving: false,
      loading: false,
      originalDraft: false,
      itemProgress: 100,
      valid: true,
      expanded: [],
      moveOffset: 0,
      subTotal: 0,
      selectedItem: null,
      restrictedToSalesRep: '',
      lines: v => v.length>0 || 'There must be at least one line item',
      selectCustomer: v => !!v || 'Select a customer',
      itemValid: id => id !== '' || 'Please select',
      inputLine: null,
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    sites() {
      return this.$store.getters.sites(!this.shipmentId);
    },
    customer() {
      return this.customers.find(customer => customer.id === this.shipment.customerId);
    },
    customersList() {
      return this.restrictedToSalesRep
              ? this.customers.filter(customer => customer.salesRep?.id === this.restrictedToSalesRep ||
                                                  customer.id === this.shipment.customerId)
              : this.customers;
    },
    bulkContainers() {
      return this.allBulkContainers.filter(bulkContainer => bulkContainer.contents.siteId === this.shipment.siteId);
    },
    tanks() {
      return this.allTanks.filter(tank => tank.contents.siteId === this.shipment.siteId);
    },
    types() {
      const types = [];
      types.push({name:'Product',id:'Product',menu:this.menuForProducts(this.products)});
      if (this.bundles.length)
        types.push({name:'Bundle',id:'Bundle',menu:this.bundles});
      if (this.items.length) {
        if (this.itemCategories.length) {
          const categories = this.createHierarchyList(this.itemCategories);
          for (const category of categories) {
            const sub = this.items.filter(item => item.category?.id === category.id);
            if (sub.length)
              category.menu = sub;
            else
              category.disabled = true;
          }
//          types.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
          types.push({name:'Item',id:'Item',menu:categories});
          const leftOver = this.items.filter(item => !item.category?.id);
          if (leftOver.length)
            types[types.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
        }
        else
          types.push({name:'Item',id:'Item',menu:this.items});
      }
      if (this.$store.getters.company.permissions.includes('production'))
        types.push({name:'Bulk alcohol',id:'alcohol',
                    menu:[{name:'Bulk containers',id:'BulkContainer',menu:this.vesselsMenu(this.bulkContainers)},
                          {name:'Tank',id:'Tank',menu:this.vesselsMenu(this.tanks)}]});
      return types;
    },
    addOkay() {
      return this.types.length;
    },
    values() {
      return this.company.permissions.includes('values');
    },
    lineHeaders() {
      const headers =  [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true},
                        { text: 'Item / Description', value: 'item', width:'30%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                        { text: 'SKU', value: 'sku' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true},
                        { text: 'Quantity', value: 'quantity' ,width:'12.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'}
                        ];
      if (!this.recurring)
        headers.push({ text: 'Sales order #', value: 'so' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',align:'right',divider:true});
      if (this.createInvoice && this.values)
        headers.push( { text: 'Unit price', value: 'price' ,width:'10%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Amount', value: 'amount' ,width:'12%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true,align:'right'},
                      { text: 'Tax', value: 'taxes' ,width:'10.5%',class:'pl-2',cellClass:'py-2 px-1 pt-table-top',divider:true});
      return headers;
    },
    alreadyInvoiced() {
      return !this.originalInvoice && this.shipment.lines.some(line =>
        line.salesOrderId &&
        this.getSourceItem(line)?.outstandingSalesOrdersForShipping?.find(
                                    so => so.customerId === this.customer?.id &&
                                          (line.type!=='Product' || line.inBondProduct===so.inBondProduct) &&
                                          so.salesOrderId === line.salesOrderId)?.invoiced
      )
    },
    actualSubtotal() {
      return this.invoice.taxInclusive
              ? this.$roundNumber(this.shipment.lines.reduce((total,line) =>
                    total + line.amount/ (1+(this.taxes.find(tax => tax.id === line.taxCodeId)?.salesRate??0)/100), 0))
              : this.subTotal;
    },
    discountValue() {
      if (this.invoice.discount.percent)
        return this.$roundNumber(this.actualSubtotal * this.invoice.discount.amount/100);
      return this.invoice.discount.amount;
    },
    total() {
      return this.actualSubtotal - this.discountValue + this.shipment.taxLines.reduce((total,tax) => tax.amount + total, 0);
    }
  },
  watch: {
    shipment: {
      handler() {this.dirty = true},
      immediate: true,
      deep: true
    },
    alreadyInvoiced() {
      if (this.alreadyInvoiced)
        this.createInvoice = false;
    }
  },
  async created() {
    this.loading = true;
    if (this.shipmentId || this.salesOrderId || this.copyShipmentId)
      this.itemProgress = 0;
    try {
      this.province = await import(`@/views/provinces/${this.company.province}/sales/shipments.js`);
    // eslint-disable-next-line no-empty
    } catch (error) {}

    const data = await Promise.all([
      this.$store.dispatch('customers', !this.shipmentId),
      this.$store.dispatch('products'),
      this.$store.dispatch('items'),
      this.$lazyListLoad(this.productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.products, 'products2', 'id,name,upc,scc,category{id}', 100, !this.shipmentId ? [{field:'active', comparator:'eq', value:'true'}] : []),
      this.$lazyListLoad(this.bundles, 'bundles2', 'id,name,price,inBondPrice,tax{id},sellingDescription,sku,displayComponents', 100, !this.shipmentId ? [{field:'active', comparator:'eq', value:'true'}] : []),
//      this.$lazyListLoad(this.items, 'items2', 'id,name,lotTracking,trackUnits,precision,upc,category{id}', 100, [{field:'sold', comparator:'eq', value:'true'}, ...(!this.shipmentId ? [{field:'active', comparator:'eq', value:'true'}] : [])]),
      this.$lazyListLoad(this.spirits, 'spirits2', 'id,name', 100, this.shipmentId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.company.allowWine ?  this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, this.shipmentId ? [] : [{field:'active', comparator:'eq', value:'true'}]) : [],
      this.$lazyListLoad(this.nonExcises, 'nonExcises2', 'id,name', 100, this.shipmentId ? [] : [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.taxes, 'taxes2', 'id,name,salesRate,groupTaxes', 100, this.shipmentId ? [] : [{field:'active', comparator:'eq', value:'true'},{field:'salesRate', comparator:'ne', value:''}])
    ]);
    this.customers = data[0];
    this.products = this.shipmentId ? data[1] : data[1].filter(product => product.active);
    this.items = this.shipmentId ? data[2].filter(item => item.sold) : data[2].filter(item => item.sold && item.active);

    const results = await this.$apiCall(`query SaleShipmentsEdit($edit:Boolean!, $copy:Boolean!, $id:ID!, $salesOrder:Boolean!, $salesOrderId:ID!, $estimateId:ID!, $estimate:Boolean!) {
                                              shipment(id:$id) @include(if:$edit) {
                                                id,customer{id},shipToAddress,billToAddress,email,cc,bcc,date,draft,delivered,memo,privateNote,
                                                number,pallets,specialInstructions
                                                site{id},shipVia,po
                                                attachmentURL
                                                invoice {id,date,number,paymentMethodId,accountRef,termId,salesRepId,due,qbo,allowOnlinePayments,paid,taxInclusive,exchangeRate,discount {percent, amount}}
                                                lines {
                                                  type,id,inBondProduct,cases,description,sku,quantity,salesOrderId,lot,unitPrice,amount,taxCodeId
                                                  items {type,id,inBondProduct,cases,description,sku,quantity,salesOrderId,lot,unitPrice,amount,taxCodeId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                recurringTransaction {
                                                  name,scheduleType,advanceDays,startDate,scheduleInterval
                                                  frequency,dayOfWeek,ordinal,day,month
                                                  end,endDate,endOccurances
                                                }
                                                shipmentZone{id}
                                              }
                                              salesOrder(id:$salesOrderId) @include(if:$salesOrder) {
                                                customerId,shipToAddress,billToAddress,email,cc,bcc,siteId,termId,salesRepId,po,shipVia,picked
                                                shipmentZoneId
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,shippedQuantity,unitPrice,amount,taxCodeId
                                                  items {type,id,cases,inBondProduct,description,sku,quantity,shippedQuantity,unitPrice,amount,taxCodeId}
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                discount {percent, amount}
                                              }
                                              estimate(id:$estimateId) @include(if:$estimate) {
                                                customer{id},shipToAddress,billToAddress,email,cc,bcc,memo,privateNote,
                                                salesRep{id},taxInclusive
                                                lines {
                                                  type,id,cases,inBondProduct,description,sku,quantity,amount,taxCodeId,unitPrice
                                                }
                                                taxLines {
                                                  id,name,amount,onAmount,percent
                                                }
                                                discount {percent, amount}
                                              }
                                              shipmentNextNumber
                                              invoiceNextNumber @include(if:$copy)
                                              salesPreferences {
                                                defaultMessage,usePicking,pickBeforeShip
                                                defaultCreateInvoice,allowDiscounts,alwaysInBond
                                                useNumber,defaultQBO,defaultAllowOnlinePayments,defaultTaxInclusive
                                                defaultCc,defaultBcc,defaultCcSalesRep,defaultBccSalesRep
                                              }
                                              shipVias {
                                                name
                                              }
                                          }`,
                                        {edit:!!this.shipmentId || !!this.copyShipmentId,
                                         copy:!!this.copyShipmentId,
                                         id:this.shipmentId ?? this.copyShipmentId ?? '',
                                         salesOrder:!!this.salesOrderId,salesOrderId:this.salesOrderId || '',
                                         estimate:!!this.estimateId, estimateId:this.estimateId??''}
                                         );
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }

    this.preferences = results.data.salesPreferences;
    this.shipVias = results.data.shipVias.map(shipVia => shipVia.name);
    await this.getVessels();      // Refresh list of bulk containers and tanks

    if (this.connectedToAccounting) {
      if (this.shipmentId && !this.values)
        this.$store.commit('setError', {message:'You do not have permission to make changes to this shipment.', type:'warning'});
    }

    if (this.shipmentId || this.copyShipmentId) {
      let key = Date.now();
      results.data.shipment.lines.forEach(item => {
        item.key = key++;
        if (this.copyShipmentId) {
          item.salesOrderId = '';
          item.lot = '';
        }
      });
      if (results.data.shipment.invoice) {
        this.invoice = results.data.shipment.invoice;
        if (this.copyShipmentId) {
          delete this.invoice.id;
          this.invoice.number = results.data.invoiceNextNumber;
        }
        this.invoice.draft = results.data.shipment.draft;
        this.paid = this.invoice.paid;
        delete this.invoice.paid;
        this.createInvoice = true;
        if (this.shipmentId)
          this.originalInvoice = true;
      }
      results.data.shipment.customerId = results.data.shipment.customer.id;
      results.data.shipment.siteId = results.data.shipment.site.id;
      results.data.shipment.shipmentZoneId = results.data.shipment.shipmentZone?.id;
      delete results.data.shipment.customer;
      delete results.data.shipment.site;
      delete results.data.shipment.shipmentZone;
      this.shipment = this.shipmentId ? results.data.shipment : {...results.data.shipment, id:'', date:this.shipment.date, po:''};
      let n = 0;
      for (const line of this.shipment.lines) {
        if (line.type === 'BulkContainer' || line.type === 'Tank')
          line.type = 'alcohol';
        await this.setLineDetails(line, true);

        if (this.invoice.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.invoice.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
        this.itemProgress = (n++ / this.shipment.lines.length) * 100;
      }
      this.calculateTotals(!this.shipment.taxLines);
      this.recurring = !!this.shipment.recurringTransaction && !this.copyShipmentId;
      this.recurringTransaction = this.shipment.recurringTransaction ?? {};
      this.originalDraft = this.shipment.draft;
      this.edit = this.originalDraft || !!this.copyShipmentId || this.recurring;
//      if (this.shipment.draft)
//        this.createInvoice = this.preferences.defaultCreateInvoice;
    }
    else {
      this.edit = true;
      this.invoice.taxInclusive = this.preferences.defaultTaxInclusive;
      this.shipment.number = results.data.shipmentNextNumber;
      if (this.salesOrderId || this.estimateId) {
        const copyFrom = results.data.salesOrder ?? results.data.estimate;
        this.shipment.customerId = copyFrom.customerId ?? copyFrom.customer.id;
        this.shipment.billToAddress = copyFrom.billToAddress;
        this.shipment.shipToAddress = copyFrom.shipToAddress;
        this.shipment.email = copyFrom.email;
        this.shipment.cc = copyFrom.cc;
        this.shipment.bcc = copyFrom.bcc;
        this.shipment.siteId = copyFrom.siteId || this.sites[0].id;
        this.shipment.po = copyFrom.po ?? '';
        this.shipment.shipVia = copyFrom.shipVia ?? '';
        this.invoice.discount = copyFrom.discount;
        this.shipment.taxLines = copyFrom.taxLines;
        this.shipment.shipmentZoneId = copyFrom.shipmentZoneId;
        let n = 0;
        for (const copyFromLine of copyFrom.lines) {
          const quantity = copyFromLine.quantity - (copyFromLine.shippedQuantity ?? 0)
          if (quantity) {
            if (copyFromLine.type === 'Spirit' || copyFromLine.type === 'Wine' || copyFromLine.type === 'NonExcise') {
              copyFromLine.id = this.tanks.find(tank => tank.contents.alcoholType === copyFromLine.type && tank.contents.alcoholId === copyFromLine.id)?.id ||
                          this.bulkContainers.find(bc => bc.contents.alcoholType === copyFromLine.type && bc.contents.alcoholId === copyFromLine.id)?.id ||
                          this.tanks[0].id;
              copyFromLine.type = 'alcohol';
            }
            const amount = copyFromLine.amount ? this.$roundNumber(quantity / copyFromLine.quantity * copyFromLine.amount) : 0;
            const line = {type:copyFromLine.type,id:copyFromLine.id,inBondProduct:copyFromLine.inBondProduct,cases:copyFromLine.cases,description:copyFromLine.description,sku:copyFromLine.sku,quantity,
                          salesOrderId:this.salesOrderId ?? '',
                          amount,taxCodeId:copyFromLine.taxCodeId,lot:null,unitPrice:amount/quantity,items:copyFromLine.items};
            await this.setLineDetails(line, true);
            this.shipment.lines.push(line);
            if (this.invoice.taxInclusive && line.taxCodeId) {
              const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
              line.unitPrice += line.unitPrice * tax.salesRate/100;
              line.amount = this.$roundNumber(line.unitPrice * line.quantity);
            }
          }
          this.itemProgress = (n++ / copyFrom.lines.length) * 100;
        }
        this.invoice.termId = copyFrom.termId ?? '';
        this.invoice.salesRepId = copyFrom.salesRepId ?? copyFrom.salesRep?.id ?? '';
        this.calculateTotals(!this.shipment.taxLines);
      }
      else {
        this.shipment.customerId = this.customerId;
        this.changeCustomer();
        this.shipment.siteId = this.sites[0].id;
        this.shipment.cc = this.preferences.defaultCc;
        this.shipment.bcc = this.preferences.defaultBcc;
      }
      this.shipment.memo = this.preferences.defaultMessage;
      this.createInvoice = this.preferences.defaultCreateInvoice;
    }
    if (this.copyShipmentId) {
      this.shipment.date = this.date ? `${this.date} 00:00:00` : format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      this.shipment.number = results.data.shipmentNextNumber;
    }
    this.customerTypes = await this.buildCustomerTypes();
    this.$nextTick(() => {this.dirty = false});
    this.itemProgress = 100;
    this.loading = false;
  },

  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.lineTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        oldIndex -= _self.moveOffset;
        const lineSelected = _self.shipment.lines.splice(oldIndex, 1)[0];
        _self.shipment.lines.splice(newIndex, 0, lineSelected);
        _self.buildExpandedList();
      },
      onStart({oldIndex}) {
        let offset = 0;
        for (let index = 0; index < oldIndex-offset; index++) {
          const line = _self.shipment.lines[index];
          if (_self.expanded.some(e => e.key === line.key))
            offset += 1;
        }
        _self.moveOffset = offset;
        _self.expanded.splice(0, _self.expanded.length);
      }
    })

    if (!this.shipmentId)
      this.$refs.form?.validate();
    setTimeout(() => this.$refs.customer.focus(), 1000);
  },
  beforeDestroy() {
    this.sortable.destroy();
  },

  methods: {
    async scan({type, id, quantity, cases, lot}) {
      const existingLine = this.shipment.lines.find(line => line.type === type && line.id === id && line.cases === cases && line.lot === lot);
      if (existingLine) {
        existingLine.quantity += quantity;
        this.changeQuantityPrice(existingLine);
      }
      else {
        const line = await this.addLine();
        line.type = type;
        line.id = id;
        line.lot = lot;
        line.quantity = quantity;
        await this.setLineDetails(line);
        line.cases = cases;
        this.changeProductBonded(line); // Need to do this explicitly since setLineDetails could set line.cases based on preferences
      }
    },
    itemDescription(item) {
      const type = this.types.find(type => type.id === item.type);
      if (!type)
        return 'MISSING';

      const lists = {'Product':[this.products], 'Item': [this.items], 'Bundle': [this.bundles], 'alcohol':[this.bulkContainers,this.tanks]};
      let name;
      for (const list of lists[type.id]) {
        name = list.find(i => i.id === item.id)
        if (name)
          break;
      }

      return `${type.name}: ${name?.name}`;
    },
    itemTax(item) {
      if (this.connectedToAccounting && !item.taxCodeId)
        return 'Required';
      return this.taxes.find(tax => tax.id === item.taxCodeId)?.name;
    },
    isBulkContainer(line) {
      return line.type === 'alcohol' && this.bulkContainers.some(b => b.id === line.id);
    },
    menuForProducts(products) {
      if (this.productCategories.length) {
        const categories = this.createHierarchyList([...this.productCategories]);
        for (const category of categories) {
          const sub = products.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//        const p = [...categories.filter(category => !category.disabled)];
        const p = categories;
        const leftOver = products.filter(product => !product.category?.id);
        if (leftOver.length)
          p.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
        return p;
      }
      else
        return products;
    },
    vesselsMenu(vessels) {
      vessels.forEach(vessel => {
        if (vessel.contents) {
          let amount = '(empty)';
          if (vessel.contents.alcoholType && vessel.contents.volume) {
            const spirits = vessel.contents.alcoholType === 'Spirit';
            amount = `${this.$number(vessel.contents.volume, 3)} ${spirits?'LAA':'litres'} ${vessel.contents.alcoholName}`;
          }
          vessel.subTitle = amount;
        }
      });
      return vessels;
    },
    calculateTotals(calculateTaxes) {
      this.subTotal = this.shipment.lines.reduce((total,line) =>
        total + (line.items ? line.items.reduce((total,line) => total+line.amount, 0) : line.amount)
      , 0);

      if (calculateTaxes) {
        this.shipment.taxLines = [];
        this.shipment.lines.forEach(line => {
          if (line.items)    // Process bundle items
            line.items.forEach(item => this.calculateTaxesOnLine(item));
          else
            this.calculateTaxesOnLine(line);
        });
      }
    },
    calculateTaxesOnLine(line) {
      const discount = 1 - (this.subTotal ? (this.invoice.discount.percent ? this.invoice.discount.amount/100 : this.invoice.discount.amount / this.subTotal) : 0);
      if (line.taxCodeId) {
        const taxDefinition = this.taxes.find(t => t.id === line.taxCodeId);
        if (!taxDefinition)
            return;
        for (const taxCodeId of taxDefinition.groupTaxes ?? [line.taxCodeId]) {
          let tax = this.shipment.taxLines.find(tax => tax.id === taxCodeId);
          if (!tax) {
            const taxDefinitionToUse = this.taxes.find(t => t.id === taxCodeId);
            if (!taxDefinitionToUse)
                return;
            tax = {id:taxCodeId,name:taxDefinitionToUse.name,percent:taxDefinitionToUse.salesRate,amount:0,onAmount:0};
            this.shipment.taxLines.push(tax);
          }
          tax.onAmount += discount * (this.invoice.taxInclusive ? line.amount/(1+tax.percent/100) : line.amount);
          tax.amount += this.$roundNumber(discount * (this.invoice.taxInclusive ? line.amount - line.amount/(1+tax.percent/100) : line.amount * tax.percent / 100));
        }
      }
    },
    lotTracking(line) {
      if (line.type === 'Product') {
        if (line.inBondProduct)
          return this.company.settings.lotTrackingForProducts;
        else
          return this.company.settings.lotTrackingForDutyPaid;
      }
      if (line.type === 'Item')
        return this.items.find(item => item.id === line.id)?.lotTracking;
      return false;
    },
    currentLots(line) {
      const list = {Item:this.items,Product:this.products}[line.type];
      const obj = list?.find(item => item.id === line.id);
      return obj?.sites?.filter(site => site.siteId === this.shipment.siteId).map(site =>
        line.type === 'Item' ? {lot:site.lot,quantity:site.quantity}
                             : {lot:site.lot,quantity:Math.floor((line.inBondProduct?site.inBondBottles:site.dutyPaidBottles)/(line.cases?obj.caseSize:1))}
      ) ?? [];
    },
    changeSite() {
      this.shipment.lines = this.shipment.lines.filter(line => this.types.some(t => t.id === line.type));
    },
    suffix(line) {
      if (line.type === 'Product') {
        if (line.cases)
          return 'case(s)';
        const product = this.products.find(p => p.id === line.id);
        if (product?.bottlesPerUnit > 1)
          return 'unit(s)';
        if (product?.mixedPack)
          return 'pack(s)';
        if (product?.containerType === 'Can')
          return 'can(s)';
        return 'bottle(s)';
      }
      if (line.type === 'alcohol') {
        const contents = this.vesselContents(line);
        return contents.alcoholType === 'Spirit' ? 'LAA' : 'litres';
      }
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.trackUnits;
      if (line.type === 'Bundle')
        return '';
      return null;
    },
    vesselContents(line) {
      const vessel = this.tanks.find(tank => tank.id === line.id) ?? this.bulkContainers.find(bc => bc.id === line.id);
      return vessel.contents;
    },
    precision(line) {
      if (line.type === 'Product')
        return 0;
      if (line.type === 'Item')
        return this.items.find(i => i.id === line.id)?.precision;
      if (line.type === 'Bundle')
        return 0;
      return 3;
    },
    customerHint(customer) {
      const noType = customer?.type === 'None';
      return `${noType ? '' : `Type: ${this.customerTypes.find(type => type.value === customer?.type)?.text}`}${customer?.category?.id ? `${noType?'': ', '}Category: ${customer.category.name}` : ''}`;
    },
    async addLine() {
      const line = {key:Date.now(),type:'',id:'',inBondProduct:this.preferences.alwaysInBond,cases:false,description:'',errorMessage:null,amount:0,unitPrice:0,quantity:1,taxCodeId:null};
      this.shipment.lines.push(line);
      this.setLineDetails(line, false);
      this.selectedItem = line.key;
      this.$nextTick(() => this.$refs.form?.validate());
      return line;
    },
    deleteLine(index) {
      this.shipment.lines.splice(index, 1);
      this.buildExpandedList();
      this.calculateTotals(true);
    },

    async getVessels() {
      if (!this.$store.getters.company.permissions.includes('production'))
        return;
      this.allTanks = [];
      this.allBulkContainers = [];
      await Promise.all([
        this.$lazyListLoad(this.allTanks, 'tanks2', `id,name,contents{siteId,volume,alcoholType,alcoholId,alcoholName}`, 100, this.shipmentId?[]:[{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.shipment.date}]),
        this.$lazyListLoad(this.allBulkContainers, 'bulkContainers2', `id,name,contents{siteId,volume,alcoholType,alcoholId,alcoholName}`, 100, this.shipmentId?[]:[{field:'contents.volume', comparator:'ne', value:'0'}], [{name:'asof', value:this.shipment.date}])
      ]);
    },

    async changeDate() {
      this.invoice.date = this.shipment.date.substring(0, 10);
      await this.getVessels();      // Refresh list of bulk containers and tanks
      for (const line of this.shipment.lines) {   // in order to get new lot info for date
        if (line.type === 'Product')
          await this.loadProduct(line);
        else if (line.type === 'Item')
          await this.loadItem(line);
      }
    },

    buildExpandedList() {
      this.expanded.splice(0, this.expanded.length);
      for (const line of this.shipment.lines) {
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents)
            this.expanded.push(line);
        }
      }
    },

    async loadProduct(line) {
      const product = this.products.find(product => product.id === line.id);
      if (!product) {
        line.type = '';
        line.id = '';
        return;
      }
      if (product.loaded === this.shipment.date)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesShipmentsEdit($id:ID!,$date:DateTime!,$edit:Boolean!) {
                                            product(id:$id) {
                                              sku,bottleSize,caseSize,bottlesPerUnit
                                              mixedPack,containerType
                                              defaultBondedPrice,defaultBondedTax{id},sellingBondedDescription,
                                              defaultPrice,defaultTax{id},sellingDescription,
                                              sites(asof:$date) {siteId,lot,inBondBottles,dutyPaidBottles},
                                              bondedCustomers{customerId,tax{id},price,sku}
                                              customers{customerId,tax{id},price,sku}
                                              outstandingSalesOrdersForShipping(edit:$edit) {
                                                salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId,inBondProduct,invoiced
                                              }
                                            }
                                          }`,
                                          {id:product.id,date:this.shipment.date,edit:this.shipmentId!==undefined});
      this.loading = false;
      if (!results.error) {
        Object.assign(product, results.data.product);
        product.loaded = this.shipment.date;
      }
    },

    async loadItem(line) {
      const item = this.items.find(item => item.id === line.id);
      if (!item) {
        line.type = '';
        line.id = '';
        return;
      }
      if (item.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesShipmentsEdit($id:ID!,$date:DateTime!,$edit:Boolean!) {
                                            item(id:$id) {
                                              sku,defaultPrice,defaultTax{id},sellingDescription,lotTracking
                                              sites(asof:$date) {siteId,lot,quantity},
                                              customers{customerId,tax{id},price,sku}
                                              outstandingSalesOrdersForShipping(edit:$edit) {
                                                salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId,invoiced
                                              }
                                            }
                                          }`,
                                          {id:item.id,date:this.shipment.date,edit:this.shipmentId!==undefined});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data.item);
        item.loaded = true;
      }
    },

    async loadAlcoholItem(line) {
      const item = this.getSourceItem(line);
      if (!item) {
        line.type = '';
        line.id = '';
        return;
      }
      if (item.loaded)
        return;

      if (item.type === null)
        return;
      let type = item.type.toLowerCase();
      type = {nonexcise:'nonExcise',winehigh:'wine',winemedium:'wine',wineweak:'wine'}[type] ?? type;

      this.loading = true;
      const results = await this.$apiCall(`query SalesShipmentsEdit($id:ID!,$edit:Boolean!) {
                                             ${type}(id:$id) {
                                               sku,defaultPrice,defaultTax{id},sellingDescription,customers{customerId,tax{id},price,sku}
                                               outstandingSalesOrdersForShipping(edit:$edit) {
                                                 salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId,invoiced
                                               }
                                              }
                                            }`,
                                          {id:item.id,edit:this.shipmentId!==undefined});
      this.loading = false;
      if (!results.error) {
        Object.assign(item, results.data[type]);
        item.loaded = true;
      }
    },

    async loadBundle(line) {
      const bundle = this.bundles.find(bundle => bundle.id === line.id);
      if (!bundle) {
        line.type = '';
        line.id = '';
        return;
      }
      if (bundle.loaded)
        return;

      this.loading = true;
      const results = await this.$apiCall(`query SalesShipmentsEdit($id:ID!,$edit:Boolean!) {
                                            bundle(id:$id) {
                                              items {name,type,id,quantity,price}
                                              outstandingSalesOrdersForShipping(edit:$edit) {
                                                salesOrderId,customerId,soNumber,soDate,quantityOutstanding,unitCost,taxCodeId,invoiced
                                              }
                                            }
                                          }`,
                                          {id:bundle.id,edit:this.shipmentId!==undefined});
      this.loading = false;
      if (!results.error) {
        Object.assign(bundle, results.data.bundle);
        bundle.loaded = true;
      }
    },

    changeLineType(line) {
      line.unitPrice = 0;
      line.taxCodeId = null;
      line.description = '';
      line.sku = '';
      this.setLineDetails(line, false);
    },

    getSourceItem(line) {
      const list = {'Item':this.items,'Spirit':this.spirits,'Wine':this.wines,'WineHigh':this.wines,'WineMedium':this.wines,'WineWeak':this.wines,'NonExcise':this.nonExcises,'Product':this.products,'Bundle':this.bundles};
      if (line.type === 'alcohol') {
        const contents = this.vesselContents(line);
        const item = list[contents.alcoholType]?.find(item => item.id === contents.alcoholId) ?? {};
        item.type = contents.alcoholType;
        return item;
      }
      return list[line.type]?.find(item => item.id === line.id) ?? {};
    },

    async setLineDetails(line, init) {
      if (line.type !== 'Bundle')
        delete line.items;

      if (line.id === '') {
        this.$nextTick(() => this.$refs.form?.validate());
        return;
      }

      if (line.type === 'Product')
        await this.loadProduct(line);
      else if (line.type === 'Item')
        await this.loadItem(line);
      else if (line.type === 'Bundle')
        await this.loadBundle(line);
      else if (line.type === 'alcohol')
        await this.loadAlcoholItem(line);

      this.buildExpandedList();

      if (!init) {
        if (!this.getSourceItem(line)?.outstandingSalesOrdersForShipping?.some(
                                      so => so.customerId === this.customer?.id &&
                                            (line.type!=='Product' || line.inBondProduct===so.inBondProduct) &&
                                            so.salesOrderId === line.salesOrderId))

          line.salesOrderId = '';
        let customerPricing;
        if (line.type === 'Bundle') {
          const bundle = this.bundles.find(bundle => bundle.id === line.id);
          if (bundle.displayComponents) {
            this.$set(line, "items", []);
            let key = Date.now();
            for (const item of bundle.items) {
              const bundleLine = {key:key++,type:item.type,name:item.name,id:item.id,description:'',errorMessage:null,
                                  amount:0,unitPrice:item.price,quantity:item.quantity,taxCodeId:null};
              line.items.push(bundleLine);
              this.setLineDetails(bundleLine, false);
            }
          }
          else {
            delete line.items;
            line.unitPrice = !this.company.settings.lotTrackingForProducts && this.customerTypes.find(type => type.value === this.customer?.type)?.prefInBond ? bundle.inBondPrice : bundle.price;
            line.taxCodeId = bundle.tax?.id;
            this.calculateUnitPrice(line);
            line.sku = bundle.sku;
            this.changeQuantityPrice(line);
          }
          line.description = bundle.sellingDescription || bundle.name;
        }
        else if (line.type === 'Product') {
          line.inBondProduct = !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond || this.preferences.alwaysInBond;
          line.cases = this.customerTypes.find(type => type.value === this.customer?.type)?.prefCases ?? false;
          this.changeProductBonded(line);
        }

        else if (line.type === 'Item') {
          const item = this.items.find(item => item.id === line.id);
          customerPricing = item.customers.find(customer => customer.customerId === this.shipment.customerId) ??
                            item.customers.find(customer => customer.customerId === this.customer?.category?.id);
          line.unitPrice = customerPricing?.price || item.defaultPrice;
          line.taxCodeId = customerPricing?.tax.id || item.defaultTax?.id;
          this.calculateUnitPrice(line);
          line.description = line.description || item.sellingDescription || item.name;
          line.sku = customerPricing?.sku || item?.sku;
          this.changeQuantityPrice(line);
        }

        else if (line.type === 'alcohol') {
          const contents = this.vesselContents(line);
          line.quantity = contents.volume;
          if (line.quantity) {
            const alcohol = this.spirits.find(alcohol => alcohol.id === contents.alcoholId) ||
                            this.wines.find(alcohol => alcohol.id === contents.alcoholId) ||
                            this.nonExcises.find(alcohol => alcohol.id === contents.alcoholId);
            customerPricing = alcohol.customers.find(customer => customer.customerId === this.shipment.customerId) ??
                               alcohol.customers.find(customer => customer.customerId === this.customer?.category?.id);
            line.unitPrice = customerPricing?.price || alcohol.defaultPrice;
            line.taxCodeId= customerPricing?.tax.id || alcohol.defaultTax?.id;
            this.calculateUnitPrice(line);
            line.description = line.description || alcohol.sellingDescription || alcohol.name;
            line.sku = customerPricing?.sku || alcohol?.sku;
            this.changeQuantityPrice(line);
          }
        }
      }
      this.$nextTick(() => this.$refs.form?.validate());
    },
    salesOrdersForLine(line) {
      return [{salesOrderId:'',soNumber:''},...this.getSourceItem(line)?.outstandingSalesOrdersForShipping?.filter(so => so.customerId === this.customer?.id &&
                                                                                                            (line.type!=='Product' || line.inBondProduct===so.inBondProduct)) ?? []];
    },
    salesOrderForLine(line) {
      return this.salesOrdersForLine(line).find(so => so.salesOrderId === line.salesOrderId)?.soNumber ?? '';
    },
    changedSO(line) {
      const so = this.salesOrdersForLine(line).find(so => so.salesOrderId === line.salesOrderId);
      line.quantity = so.quantityOutstanding ?? line.quantity;
      line.amount = so.unitCost ? line.quantity * so.unitCost : line.amount;
      line.unitPrice = so.unitCost ? so.unitCost : (line.quantity ? line.amount / line.quantity : 0);
      line.taxCodeId = so.taxCodeId ?? line.taxCodeId;
      this.changeQuantityPrice(line);
    },
    isProductLoaded(line) {
      return this.products.find(product => product.id === line.id)?.loaded;
    },
    changeProductBonded(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = line.inBondProduct && !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond;
      line.description = product[inBondPricing?'sellingBondedDescription':'sellingDescription'] || product.name;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      if (productCustomers === undefined)
          this.$apiCall(`mutation ClientError($input:ClientErrorInput!) {
                              logClientError(input:$input)
                            }`,{input:{query:'DEBUG ERROR', variables:'', product,
                                        error:`Product ${product.name} has no ${inBondPricing ? 'bonded' : 'non-bonded'} customers`}});
      const customerPricing = productCustomers.find(customer => customer.customerId === this.shipment.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.sku = customerPricing?.sku || product?.sku;
      this.setProductPrice(line);
      this.$nextTick(() => this.$refs.form.validate());
    },
    async setProductPrice(line) {
      const product = this.products.find(product => product.id === line.id);
      const inBondPricing = line.inBondProduct && !!this.customer && this.customerTypes.find(type => type.value === this.customer.type)?.prefInBond;
      const productCustomers = product[inBondPricing ? 'bondedCustomers' : 'customers'];
      if (productCustomers === undefined)
          this.$apiCall(`mutation ClientError($input:ClientErrorInput!) {
                              logClientError(input:$input)
                            }`,{input:{query:'DEBUG ERROR', variables:'', product,
                                        error:`Product ${product.name} has no ${inBondPricing ? 'bonded' : 'non-bonded'} customers`}});
      const customerPricing = productCustomers.find(customer => customer.customerId === this.shipment.customerId) ??
                              productCustomers.find(customer => customer.customerId === this.customer?.category?.id);
      line.unitPrice = customerPricing?.price || product[inBondPricing?'defaultBondedPrice':'defaultPrice'];
      line.taxCodeId = customerPricing?.tax.id || product[inBondPricing?'defaultBondedTax':'defaultTax']?.id;
      if (!customerPricing && this.province && !inBondPricing)
        await this.province.pricing(line, product, this.customer, this.taxes);
      if (inBondPricing) {
        if (!line.cases)
          line.unitPrice = line.unitPrice / product.caseSize;
      }
      else if (line.cases)
        line.unitPrice = line.unitPrice / product.bottlesPerUnit * product.caseSize;
      this.calculateUnitPrice(line);
      this.changeQuantityPrice(line);
    },
    calculateUnitPrice(line) {
      if (this.invoice.taxInclusive && line.taxCodeId) {
        const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
        line.unitPrice += line.unitPrice * tax.salesRate/100;
        line.amount = this.$roundNumber(line.unitPrice * line.quantity);
      }
    },
    toggleTaxInclusive() {
      for (const line of this.shipment.lines) {
        if (line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          if (this.invoice.taxInclusive)
            line.unitPrice += line.unitPrice * tax.salesRate/100;
          else
            line.unitPrice = line.unitPrice / (1 + tax.salesRate/100)
          line.amount = this.$roundNumber(line.unitPrice * line.quantity);
        }
      }
      this.calculateTotals(true);
    },
    async newCustomer(customer) {
      this.customers = await this.$store.dispatch('customers', !this.shipmentId);
      this.shipment.customerId = customer.id;
      this.invoice.salesRepId = this.customer.salesRep?.id;
      this.shipment.shipmentZoneId = this.customer.shipmentZone?.id;
    },
    async changeCustomer() {
      if (!this.customer) {
        this.shipment.email = '';
        this.shipment.billToAddress = '';
        this.shipment.shipToAddress = '';
        this.shipment.shipmentZoneId = '';
        return;
      }

      if (!this.customer.loaded) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesShipmentsEdit($id:ID!) {
                                                customer(id:$id) {
                                                  id,name,email,currency,term{id},salesRep{id},shipmentZone{id}
                                                  billingAddress{line1,line2,city,province,postalCode}
                                                  shippingAddress{line1,line2,city,province,postalCode}
                                                }
                                              }`,
                                              {id:this.customer.id});
        this.loading = false;
        if (!results.error) {
          this.customer.loaded = true;
          Object.assign(this.customer, results.data.customer);
        }
      }
      this.shipment.email = this.customer.email;
      const billToAddress = this.customer.billingAddress;
      this.shipment.billToAddress = this.customer.name + '\n' + billToAddress.line1 + '\n' + (billToAddress.line2 ? billToAddress.line2 + '\n' : '') +
                              billToAddress.city + ' ' + billToAddress.province + '  ' + billToAddress.postalCode;
      const shipToAddress = this.customer.shippingAddress;
      this.shipment.shipToAddress = this.customer.name + '\n' + shipToAddress.line1 + '\n' + (shipToAddress.line2 ? shipToAddress.line2 + '\n' : '') +
                              shipToAddress.city + ' ' + shipToAddress.province + '  ' + shipToAddress.postalCode;
      this.shipment.shipmentZoneId = this.customer.shipmentZone?.id;
      for (const line of this.shipment.lines)
        this.setLineDetails(line, false);
      this.calculateTotals(true);
    },
    changeQuantityPrice(line, parentLine) {
      if (line.items)
        line.items.forEach(l => this.changeQuantityPrice(l, line));
      else
        line.amount = this.$roundNumber(line.quantity * line.unitPrice * (parentLine?.quantity || 1));
      this.calculateTotals(true);
    },
    changeAmount(line) {
      line.unitPrice = line.quantity ? this.$roundNumber(line.amount / line.quantity) : 0;
      this.calculateTotals(true);
    },
    cancel() {
      this.$router.back();
    },
    isLineIncomplete(line, isInBundle) {
      return !line.type || !line.id || !line.description ||
              line.quantity < 0 && line.type !== 'Item' ||
              !isInBundle && this.lotTracking(line) && !line.lot ||
              line.unitPrice < 0 ||
              line.amount < 0 && line.type !== 'Item' ||
              (this.connectedToAccounting && this.createInvoice && !line.taxCodeId && !line.items?.length) ||
              line.items?.length && line.items.some(subLine => this.isLineIncomplete(subLine, true));
    },
    async save() {
      const incomplete = this.shipment.lines.some(line => this.isLineIncomplete(line, false));
      if (!this.$refs.form.validate() || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;

      // Build request, especially information for all items being shipped
      let hasDutyPaidProducts = false;
      // eslint-disable-next-line no-unused-vars
      const {lines,recurringTransaction,attachmentURL, ...input} = this.shipment;
      input.lines = this.shipment.lines.map(line => {
        let items;    // Only if this is a bundle with displayed items
        if (line.type === 'Bundle' && line.items) {
          items = line.items.map(bundleLine => (
             {type:bundleLine.type === 'Item' ? bundleLine.type : 'Product',
              inBondProduct:false,cases:false,id:bundleLine.id,sku:bundleLine.sku,description:bundleLine.description,
              quantity:bundleLine.quantity,amount:bundleLine.amount,taxCodeId:bundleLine.taxCodeId}
          ));
        }

        let type = line.type;
        if (type === 'Product' && !line.inBondProduct)
          hasDutyPaidProducts = true;
        else if (type === 'alcohol')
          type = this.tanks.some(tank => tank.id === line.id) ? 'Tank' : 'BulkContainer'
        let amount = line.amount;
        if (this.invoice.taxInclusive && line.taxCodeId) {
          const tax = this.taxes.find(tax => tax.id === line.taxCodeId);
          amount = this.$roundNumber(line.amount / (1 + (tax?.salesRate ?? 0)/100));
        }
        return {type,inBondProduct:line.inBondProduct,cases:line.cases,id:line.id,sku:line.sku,description:line.description,
                quantity:line.quantity,amount,taxCodeId:line.taxCodeId,lot:line.lot,
                salesOrderId:this.recurring ? '' : line.salesOrderId,
                items};
      });
      this.shipmentInput = input;

      // If we are shipping duty paid products and not doing lot tracking for duty paid products,
      // first check to see that adequate inventory exists so we can give the user a chance to
      // move inventory from in-bond, if necessary
      // Only do this on create!
//      this.moveProductToDutyPaid = null;
      if (!this.shipmentInput.draft && (!this.shipment.id || this.shipment.draft) && !this.company.settings.lotTrackingForDutyPaid && hasDutyPaidProducts) {
        const results = await this.$apiCall(`query SalesShipmentsEdit($input: ShipmentCheckDutyPaidProducts!) {
                                                shipmentCheckDutyPaidProducts(input:$input) {id,name,required,available,inBondLots}
                                              }`, {input:{date:input.date,siteId:input.siteId,lines:input.lines}});
        this.saving = false;
        if (!results.error) {
          if (results.data.shipmentCheckDutyPaidProducts.length === 0)    // All okay, sufficient inventory
            this.save2();
          else {                                                          // Move inventory
            results.data.shipmentCheckDutyPaidProducts.forEach(product => {
              product.bottles = product.required - product.available;
              product.lot = product.inBondLots.length ? product.inBondLots[0] : null;
            });
            this.moveProductToDutyPaid = results.data.shipmentCheckDutyPaidProducts;
            this.showMoveProduct = true;
          }
        }
      }
      else
        this.save2();    // Actual ship
    },
    async save2() {
      this.saving = true;

      // If moving from in-bond, create that request
      let moveInput = [];
      if (this.moveProductToDutyPaid) {
        moveInput = this.moveProductToDutyPaid.map(product => (
          {productId:product.id,date:this.shipmentInput.date,lot:product.lot,
           fromSiteId:this.shipmentInput.siteId,toSiteId:this.shipmentInput.siteId,
           cases:0,bottles:product.bottles,comments:`For shipment`}
        ));
        // Remove items with no lot as they are never valid
        moveInput = moveInput.filter(input => input.lot);
      }

      this.shipmentInput.invoice = this.createInvoice && !this.alreadyInvoiced ? this.invoice : null;
      this.shipmentInput.salesRepId = this.invoice.salesRepId;

      // If copying or existing shipment is made recurring, create a new recurring shipment (and leave original unchanged)
      if (this.copyShipmentId || this.recurring && !this.shipment.recurringTransaction)
        delete this.shipmentInput.id;

      if (this.recurring)
        this.shipmentInput.recurringTransaction = this.recurringTransaction;
      this.shipmentInput.fromRecurringTransactionId = this.fromRecurringTransactionId;

      const results = await this.$apiCall(`mutation SalesShipmentsEdit($input: ShipmentInput!, $moveInput: [ProductMoveToDutyPaidInput]!, $move:Boolean!) {
                                             productsMoveToDutyPaidCreate(input:$moveInput) @include(if:$move) {id}
                                             shipmentCreate(input:$input) {id}
                                           }`,
                                          {input:this.shipmentInput, moveInput, move:!this.shipmentInput.draft && moveInput.length!==0})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.shipment.id?'Updated':'Created'}${this.shipmentInput.draft?' draft':''} shipment to ${this.customer.name}`});
      }
    }
  }
}
</script>